import React, { useState, useEffect, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Card,
  CardBody,
  CardHeader,
  Avatar,
  Badge,
  Progress,
  IconButton,
  Tooltip,
  useToast,
  useColorModeValue,
  Container,
  SimpleGrid,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Textarea,
  Divider,
  AspectRatio,
  Image,
  useBreakpointValue,
  Spinner,
  Center,
  Icon,
  Skeleton,
  SkeletonText,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Input,
  FormControl,
  FormLabel,
  Select,
  Collapse,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Link
} from '@chakra-ui/react';
import {
  FiPlay,
  FiPause,
  FiVolume2,
  FiUser,
  FiMaximize,
  FiClock,
  FiUsers,
  FiStar,
  FiCheckCircle,
  FiLock,
  FiUnlock,
  FiVideo,
  FiFileText,
  FiMessageSquare,
  FiThumbsUp,
  FiThumbsDown,
  FiMoreVertical,
  FiChevronRight,
  FiChevronLeft,
  FiHome,
  FiArrowLeft,
  FiArrowRight,
  FiEye,
  FiCalendar,
  FiAward,
  FiTarget,
  FiTrendingUp,
  FiBook,
  FiHeadphones,
  FiMonitor,
  FiSmartphone,
  FiWifi,
  FiGlobe,
  FiChevronDown,
  FiChevronUp,
  FiSend,
  FiDownload,
  FiFile,
  FiPaperclip,
  FiLayers,
  FiPlayCircle
} from 'react-icons/fi';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { fetchCourseById } from '../services/courseService';
import VideoPlayer from '../shared/videoplayer';
import axiosInstance from '../services/axiosInstance';
import { getUserComments, getCommentsWithUserFilter } from '../services/userCommentService';
import Sidebar from '../shared/Sidebar';
import { SidebarProvider, useSidebar } from '../utils/SidebarContext';
import AuthContext from '../services/AuthContext';
import { urlvideo } from '../services/urls';

// Enhanced Animation variants with more sophisticated effects
const fadeIn = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const slideIn = {
  hidden: { opacity: 0, x: -30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.7,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1
    }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

// Enhanced color scheme with modern gradients and better contrast
const getChapterColorScheme = () => {
  return {
    chapter: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    chapterSolid: '#667eea',
    chapterHover: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
    part: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    partSolid: '#f093fb',
    partHover: 'linear-gradient(135deg, #e081e9 0%, #e3455a 100%)',
    video: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    videoSolid: '#4facfe',
    videoHover: 'linear-gradient(135deg, #3d9aec 0%, #00e0ec 100%)',
    attachment: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    attachmentSolid: '#11998e',
    attachmentHover: 'linear-gradient(135deg, #0f877c 0%, #32dd6b 100%)',
    text: '#2D3748',
    accent: '#805AD5',
    success: '#48BB78',
    warning: '#ED8936',
    error: '#F56565'
  };
};

// Enhanced Attachment Component with better visual hierarchy
const AttachmentItem = ({ attachment, onDownload }) => {
  const colorScheme = getChapterColorScheme();

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return FiFileText;
      case 'doc':
      case 'docx':
        return FiFile;
      default:
        return FiPaperclip;
    }
  };

  const getFileSize = (fileName) => {
    return 'PDF';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
    >
      <Box
        p={4}
        bg="linear-gradient(135deg, rgba(17,153,142,0.08) 0%, rgba(56,239,125,0.08) 100%)"
        _dark={{ bg: "linear-gradient(135deg, rgba(17,153,142,0.15) 0%, rgba(56,239,125,0.15) 100%)" }}
        borderRadius="xl"
        border="2px solid"
        borderColor="teal.100"
        _dark={{ borderColor: "teal.600" }}
        _hover={{
          borderColor: "teal.300",
          bg: "linear-gradient(135deg, rgba(17,153,142,0.12) 0%, rgba(56,239,125,0.12) 100%)",
          boxShadow: "0 8px 25px rgba(17,153,142,0.15)"
        }}
        transition="all 0.3s ease"
        cursor="pointer"
        onClick={() => onDownload(attachment)}
        position="relative"
        overflow="hidden"
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
          borderRadius: 'xl',
          opacity: 0,
          transition: 'opacity 0.3s ease'
        }}
        _hover={{
          _before: {
            opacity: 1
          }
        }}
      >
        <HStack spacing={4} align="center" position="relative" zIndex={1}>
          <Box
            p={3}
            borderRadius="lg"
            bgGradient={colorScheme.attachment}
            color="white"
            boxShadow="0 4px 15px rgba(17,153,142,0.25)"
            _hover={{
              transform: "scale(1.1) rotate(5deg)",
              boxShadow: "0 6px 20px rgba(17,153,142,0.35)"
            }}
            transition="all 0.3s ease"
          >
            <Icon as={getFileIcon(attachment.nom)} boxSize={6} />
          </Box>

          <VStack align="flex-start" spacing={1} flex="1">
            <Text
              fontSize="md"
              fontWeight="700"
              color="teal.700"
              _dark={{ color: "teal.200" }}
              noOfLines={2}
              lineHeight="1.3"
            >
              {attachment.nom}
            </Text>
            <HStack spacing={3} fontSize="sm" color="gray.600" _dark={{ color: "gray.400" }}>
              <Badge
                colorScheme="teal"
                size="sm"
                borderRadius="full"
                px={3}
                py={1}
                fontWeight="600"
                boxShadow="0 2px 4px rgba(0,0,0,0.1)"
              >
                {getFileSize(attachment.nom)}
              </Badge>
              <Text fontWeight="500">ملف مرفق</Text>
            </HStack>
          </VStack>

          <motion.div
            whileHover={{ scale: 1.1, rotate: 15 }}
            whileTap={{ scale: 0.9 }}
          >
            <IconButton
              icon={<FiDownload />}
              size="md"
              bgGradient={colorScheme.attachment}
              color="white"
              _hover={{
                bgGradient: colorScheme.attachmentHover,
                transform: "scale(1.05)"
              }}
              borderRadius="xl"
              boxShadow="0 4px 15px rgba(17,153,142,0.25)"
              transition="all 0.2s ease"
              aria-label="تحميل الملف"
            />
          </motion.div>
        </HStack>
      </Box>
    </motion.div>
  );
};

// Enhanced Comment status badge component
const CommentStatusBadge = ({ status }) => {
  const statusConfig = {
    pending: {
      color: 'orange',
      text: 'Pending',
      bg: 'linear-gradient(135deg, #ff9a56 0%, #ffad56 100%)'
    },
    approved: {
      color: 'green',
      text: 'Approved',
      bg: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
    },
    replied: {
      color: 'blue',
      text: 'Replied',
      bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    hidden: {
      color: 'red',
      text: 'Hidden',
      bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
    },
    public: {
      color: 'teal',
      text: 'Public',
      bg: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
    }
  };

  const config = statusConfig[status] || statusConfig.pending;

  return (
    <Badge
      bgGradient={config.bg}
      color="white"
      size="sm"
      borderRadius="full"
      px={3}
      py={1}
      fontWeight="600"
      boxShadow="0 2px 8px rgba(0,0,0,0.15)"
      textTransform="capitalize"
    >
      {config.text}
    </Badge>
  );
};

// Enhanced Reply component
const CommentReply = ({ reply, isInstructor }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Box
        ml={8}
        mt={3}
        p={4}
        bg="linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(247,250,252,0.95) 100%)"
        _dark={{ bg: "linear-gradient(135deg, rgba(45,55,72,0.9) 0%, rgba(74,85,104,0.95) 100%)" }}
        borderRadius="xl"
        borderLeft="4px solid"
        borderLeftColor={reply.isInstructor ? "purple.400" : "blue.300"}
        boxShadow="0 4px 15px rgba(0,0,0,0.08)"
        backdropFilter="blur(10px)"
        position="relative"
        _hover={{
          transform: "translateY(-1px)",
          boxShadow: "0 6px 20px rgba(0,0,0,0.12)"
        }}
        transition="all 0.3s ease"
      >
        <HStack spacing={3} align="center" mb={3}>
          <Avatar
            size="xs"
            name={reply.sender}
            border="2px solid"
            borderColor={reply.isInstructor ? "purple.300" : "blue.200"}
          />
          <Text fontSize="13px" fontWeight="600" color="gray.900" _dark={{ color: "gray.100" }}>
            @{reply.sender}
          </Text>
          {reply.isInstructor && (
            <Badge
              colorScheme="purple"
              size="xs"
              borderRadius="full"
              px={2}
              fontWeight="600"
            >
              Instructor
            </Badge>
          )}
          <Text fontSize="11px" color="gray.500" ml="auto">
            {new Date(reply.time).toLocaleDateString()}
          </Text>
        </HStack>
        <Text fontSize="14px" lineHeight="1.6" color="gray.800" _dark={{ color: "gray.200" }}>
          {reply.message}
        </Text>
      </Box>
    </motion.div>
  );
};

// Enhanced YouTube-style Comments Component
const YouTubeComments = ({
  comments,
  onCommentSubmit,
  comment,
  setComment,
  user,
  courseData,
  selectedVideo,
  onReplySubmit
}) => {
  const [sortBy, setSortBy] = useState('newest');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');

  const isInstructor = user?.role === 'instructor' ||
    user?.username === courseData?.teacherName ||
    user?._id === courseData?.teacherId;

  const getVisibleComments = () => {
    if (!user || !courseData) return [];
    return comments.filter(commentItem => {
      if (isInstructor) return true;
      const isOwnComment = commentItem.userId === user._id || commentItem.sender === user.username;
      const isPublicComment = ['approved', 'public'].includes(commentItem.status);
      return isOwnComment || isPublicComment;
    });
  };

  const visibleComments = getVisibleComments();

  const handleReplySubmit = async (commentId) => {
    if (!replyText.trim()) return;

    try {
      await onReplySubmit(commentId, replyText);
      setReplyText('');
      setReplyingTo(null);
    } catch (error) {
      console.error('Error submitting reply:', error);
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <Box
        mt={8}
        bg="linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(247,250,252,0.98) 100%)"
        _dark={{ bg: "linear-gradient(135deg, rgba(26,32,44,0.95) 0%, rgba(45,55,72,0.98) 100%)" }}
        borderRadius="3xl"
        p={8}
        boxShadow="0 20px 40px rgba(0,0,0,0.08)"
        backdropFilter="blur(20px)"
        border="1px solid"
        borderColor="rgba(255,255,255,0.3)"
        _dark={{ borderColor: "rgba(255,255,255,0.1)" }}
        position="relative"
        overflow="hidden"
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 20%, rgba(102,126,234,0.05) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(240,147,251,0.05) 0%, transparent 50%)',
          pointerEvents: 'none'
        }}
      >
        <VStack align="stretch" spacing={8} position="relative" zIndex={1}>
          {/* Enhanced Comments Header */}
          <motion.div variants={fadeIn}>
            <HStack justify="space-between" align="center">
              <HStack spacing={4}>
                <Heading size="lg" fontWeight="700" bgGradient="linear(to-r, purple.400, blue.400)" bgClip="text">
                  {visibleComments.length} Comments
                </Heading>
                <Menu>
                  <MenuButton
                    as={Button}
                    variant="ghost"
                    size="sm"
                    rightIcon={<FiChevronDown />}
                    fontWeight="600"
                    borderRadius="full"
                    _hover={{
                      bg: "linear-gradient(135deg, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)",
                      transform: "translateY(-1px)"
                    }}
                    transition="all 0.2s ease"
                  >
                    Sort by
                  </MenuButton>
                  <MenuList
                    borderRadius="xl"
                    boxShadow="0 10px 25px rgba(0,0,0,0.15)"
                    border="1px solid"
                    borderColor="rgba(255,255,255,0.2)"
                  >
                    <MenuItem
                      onClick={() => setSortBy('newest')}
                      borderRadius="lg"
                      _hover={{ bg: "purple.50" }}
                    >
                      Newest first
                    </MenuItem>
                    <MenuItem
                      onClick={() => setSortBy('oldest')}
                      borderRadius="lg"
                      _hover={{ bg: "purple.50" }}
                    >
                      Oldest first
                    </MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
            </HStack>
          </motion.div>

          {/* Enhanced Add Comment Section */}
          <motion.div variants={slideIn}>
            <HStack align="start" spacing={4}>
              <Avatar
                size="md"
                src={user?.avatar || "/assets/user-avatar.jpg"}
                name={user?.username}
                border="3px solid"
                borderColor="purple.200"
                _dark={{ borderColor: "purple.400" }}
              />
              <VStack align="stretch" spacing={4} flex="1">
                <Textarea
                  placeholder="Add a comment..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  border="2px solid transparent"
                  borderRadius="xl"
                  bg="rgba(255,255,255,0.8)"
                  _dark={{ bg: "rgba(45,55,72,0.8)" }}
                  resize="none"
                  minH="60px"
                  fontSize="15px"
                  _focus={{
                    borderColor: "purple.400",
                    boxShadow: "0 0 0 3px rgba(128,90,213,0.1)",
                    bg: "white",
                    _dark: { bg: "gray.700" }
                  }}
                  _hover={{
                    borderColor: "purple.200",
                    _dark: { borderColor: "purple.300" }
                  }}
                  transition="all 0.2s ease"
                />
                {comment && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <HStack justify="flex-end" spacing={3}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setComment('')}
                        borderRadius="full"
                        fontSize="14px"
                        fontWeight="600"
                        px={6}
                        _hover={{
                          bg: "gray.100",
                          transform: "translateY(-1px)"
                        }}
                        transition="all 0.2s ease"
                      >
                        Cancel
                      </Button>
                      <Button
                        bgGradient="linear(to-r, purple.400, blue.400)"
                        color="white"
                        size="sm"
                        _hover={{
                          bgGradient: "linear(to-r, purple.500, blue.500)",
                          transform: "translateY(-2px)",
                          boxShadow: "0 8px 20px rgba(128,90,213,0.3)"
                        }}
                        onClick={onCommentSubmit}
                        isDisabled={!comment.trim()}
                        borderRadius="full"
                        fontSize="14px"
                        fontWeight="600"
                        px={8}
                        transition="all 0.2s ease"
                      >
                        Comment
                      </Button>
                    </HStack>
                  </motion.div>
                )}
              </VStack>
            </HStack>
          </motion.div>

          {/* Enhanced Comments List */}
          <motion.div variants={staggerContainer}>
            <VStack align="stretch" spacing={6}>
              {visibleComments.length === 0 ? (
                <motion.div variants={fadeIn}>
                  <Center py={12}>
                    <VStack spacing={4}>
                      <Box
                        p={4}
                        borderRadius="full"
                        bg="linear-gradient(135deg, rgba(128,90,213,0.1) 0%, rgba(102,126,234,0.1) 100%)"
                      >
                        <Icon as={FiMessageSquare} boxSize={10} color="purple.400" />
                      </Box>
                      <Text color="gray.500" fontSize="lg" fontWeight="500">
                        No comments to display
                      </Text>
                    </VStack>
                  </Center>
                </motion.div>
              ) : (
                visibleComments.map((commentItem, index) => {
                  const isOwnComment = commentItem.user === user?._id || commentItem.sender === user?.username;
                  const commentIsInstructor = commentItem.sender === courseData?.teacherName;

                  return (
                    <motion.div
                      key={commentItem._id}
                      variants={fadeIn}
                      custom={index}
                    >
                      <Box
                        p={5}
                        bg="rgba(255,255,255,0.8)"
                        _dark={{ bg: "rgba(45,55,72,0.8)" }}
                        borderRadius="xl"
                        boxShadow="0 4px 15px rgba(0,0,0,0.08)"
                        border="1px solid"
                        borderColor="rgba(255,255,255,0.3)"
                        _dark={{ borderColor: "rgba(255,255,255,0.1)" }}
                        _hover={{
                          transform: "translateY(-2px)",
                          boxShadow: "0 8px 25px rgba(0,0,0,0.12)"
                        }}
                        transition="all 0.3s ease"
                      >
                        <HStack align="start" spacing={4}>
                          <Avatar
                            size="md"
                            name={commentItem.user?.name || commentItem.sender}
                            src={commentItem.user?.avatar || commentItem.avatar}
                            border="2px solid"
                            borderColor={commentIsInstructor ? "purple.300" : "blue.200"}
                          />
                          <VStack align="stretch" spacing={3} flex="1">
                            <HStack spacing={3} align="center" flexWrap="wrap">
                              <Text fontSize="15px" fontWeight="700" color="gray.900" _dark={{ color: "gray.100" }}>
                                @{commentItem.user?.name || commentItem.sender}
                              </Text>
                              {commentIsInstructor && (
                                <Badge
                                  bgGradient="linear(to-r, purple.400, blue.400)"
                                  color="white"
                                  size="sm"
                                  borderRadius="full"
                                  px={3}
                                  fontWeight="600"
                                >
                                  Instructor
                                </Badge>
                              )}
                              {isOwnComment && (
                                <Badge
                                  bgGradient="linear(to-r, green.400, teal.400)"
                                  color="white"
                                  size="sm"
                                  borderRadius="full"
                                  px={3}
                                  fontWeight="600"
                                >
                                  You
                                </Badge>
                              )}
                              <CommentStatusBadge status={commentItem.status} />
                              <Text fontSize="13px" color="gray.500" ml="auto">
                                {new Date(commentItem.createdAt).toLocaleDateString()}
                              </Text>
                            </HStack>

                            <Text fontSize="15px" lineHeight="1.6" color="gray.900" _dark={{ color: "gray.100" }}>
                              {commentItem.message}
                            </Text>

                            <HStack spacing={6} mt={2}>
                              <HStack
                                as="button"
                                spacing={2}
                                _hover={{
                                  color: "purple.500",
                                  transform: "scale(1.05)"
                                }}
                                transition="all 0.2s ease"
                                p={2}
                                borderRadius="lg"
                              >
                                <Icon as={FiThumbsUp} boxSize={4} />
                                <Text fontSize="13px" fontWeight="600">0</Text>
                              </HStack>
                              <HStack
                                as="button"
                                spacing={2}
                                _hover={{
                                  color: "red.400",
                                  transform: "scale(1.05)"
                                }}
                                transition="all 0.2s ease"
                                p={2}
                                borderRadius="lg"
                              >
                                <Icon as={FiThumbsDown} boxSize={4} />
                              </HStack>

                              {(isInstructor || isOwnComment) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  fontWeight="600"
                                  fontSize="13px"
                                  color="gray.600"
                                  _dark={{ color: "gray.400" }}
                                  _hover={{
                                    color: "purple.500",
                                    bg: "purple.50",
                                    transform: "translateY(-1px)"
                                  }}
                                  px={4}
                                  borderRadius="full"
                                  onClick={() => setReplyingTo(replyingTo === commentItem._id ? null : commentItem._id)}
                                  transition="all 0.2s ease"
                                >
                                  Reply
                                </Button>
                              )}
                            </HStack>

                            {replyingTo === commentItem._id && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.3 }}
                              >
                                <Box mt={4} pl={4}>
                                  <HStack spacing={3}>
                                    <Avatar
                                      size="sm"
                                      name={user?.username}
                                      src={user?.avatar}
                                      border="2px solid"
                                      borderColor="purple.200"
                                    />
                                    <Input
                                      placeholder="Write a reply..."
                                      value={replyText}
                                      onChange={(e) => setReplyText(e.target.value)}
                                      size="md"
                                      fontSize="14px"
                                      borderRadius="xl"
                                      border="2px solid transparent"
                                      _focus={{
                                        borderColor: "purple.400",
                                        boxShadow: "0 0 0 3px rgba(128,90,213,0.1)"
                                      }}
                                      _hover={{
                                        borderColor: "purple.200"
                                      }}
                                      bg="white"
                                      _dark={{ bg: "gray.700" }}
                                    />
                                    <IconButton
                                      icon={<FiSend />}
                                      size="md"
                                      bgGradient="linear(to-r, purple.400, blue.400)"
                                      color="white"
                                      _hover={{
                                        bgGradient: "linear(to-r, purple.500, blue.500)",
                                        transform: "scale(1.05)"
                                      }}
                                      onClick={() => handleReplySubmit(commentItem._id)}
                                      isDisabled={!replyText.trim()}
                                      borderRadius="xl"
                                      transition="all 0.2s ease"
                                    />
                                  </HStack>
                                </Box>
                              </motion.div>
                            )}

                            {commentItem.replies && commentItem.replies.length > 0 && (
                              <VStack align="stretch" spacing={3} mt={3}>
                                {commentItem.replies.map((reply, replyIndex) => (
                                  <CommentReply
                                    key={replyIndex}
                                    reply={reply}
                                    isInstructor={reply.isInstructor}
                                  />
                                ))}
                              </VStack>
                            )}
                          </VStack>
                        </HStack>
                      </Box>
                    </motion.div>
                  );
                })
              )}
            </VStack>
          </motion.div>
        </VStack>
      </Box>
    </motion.div>
  );
};

const CourseDetailsContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useContext(AuthContext);
  const { isOpen } = useSidebar();

  // Enhanced color palette
  const primaryColor = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
  const primarySolid = '#667eea';
  const secondaryColor = '#2D3748';
  const bgColor = useColorModeValue(
    'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)',
    'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)'
  );
  const cardBg = useColorModeValue(
    'rgba(255,255,255,0.9)',
    'rgba(45,55,72,0.9)'
  );
  const textColor = useColorModeValue('#2D3748', 'white');
  const borderColor = useColorModeValue('rgba(255,255,255,0.3)', 'rgba(255,255,255,0.1)');

  const contentMarginLeft = useBreakpointValue({ base: '0px', md: isOpen ? '280px' : '80px' });
  const isMobile = useBreakpointValue({ base: true, md: false });

  // State management
  const [courseData, setCourseData] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentChapter, setCurrentChapter] = useState(0);
  const [currentPart, setCurrentPart] = useState(0);
  const [videoProgress, setVideoProgress] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState([]);
  const [courseProgress, setCourseProgress] = useState(0);
  const [completedVideos, setCompletedVideos] = useState(new Set());
  const [isLoadingComments, setIsLoadingComments] = useState(false);

  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userComments, setUserComments] = useState([]);
  const [isLoadingUserComments, setIsLoadingUserComments] = useState(false);
  const [showUserComments, setShowUserComments] = useState(false);

  // Homework photo reply states
  const [homeworkPhotos, setHomeworkPhotos] = useState([]);
  const [myHomeworkReply, setMyHomeworkReply] = useState(null);
  const [allHomeworkReplies, setAllHomeworkReplies] = useState([]);
  const [isSubmittingHomework, setIsSubmittingHomework] = useState(false);
  const [isLoadingHomework, setIsLoadingHomework] = useState(false);

  // SMS modal states
  const [isSmsModalOpen, setIsSmsModalOpen] = useState(false);
  const [isSendingSms, setIsSendingSms] = useState(false);

  // Hierarchical navigation states
  const [sidebarView, setSidebarView] = useState('chapters'); // 'chapters' | 'parts' | 'videos'
  const [selectedChapterForNav, setSelectedChapterForNav] = useState(null);
  const [selectedPartForNav, setSelectedPartForNav] = useState(null)

  useEffect(() => {
    fetchCourseData();
  }, [id]);

  useEffect(() => {
    if (courseData && courseData.chapitres && courseData.chapitres.length > 0) {
      const firstChapter = courseData.chapitres.find(ch => ch.isOpen);
      if (firstChapter && firstChapter.parties && firstChapter.parties.length > 0) {
        const firstPart = firstChapter.parties[0];
        if (firstPart.videos && firstPart.videos.length > 0) {
          setSelectedVideo({
            ...firstPart.videos[0],
            chapterIndex: courseData.chapitres.indexOf(firstChapter),
            partIndex: 0,
            videoIndex: 0
          });
        }
      }
    }
  }, [courseData]);

  useEffect(() => {
    if (selectedVideo && selectedVideo._id && courseData) {
      fetchVideoComments(selectedVideo._id);
      fetchHomeworkData(selectedVideo._id);
    }
  }, [selectedVideo, courseData]);

  const fetchCourseData = async () => {
    try {
      setIsLoading(true);
      const response = await fetchCourseById(id);
      setCourseData(response.data);

      const totalVideos = response.data.chapitres?.reduce((acc, ch) =>
        acc + (ch.parties?.reduce((pAcc, p) => pAcc + p.videos.length, 0) || 0), 0) || 0;
      const completed = Math.floor(Math.random() * totalVideos * 0.7);
      setCourseProgress(totalVideos > 0 ? (completed / totalVideos) * 100 : 0);

    } catch (error) {
      console.error('Error fetching course:', error);
      toast({
        title: 'خطأ في تحميل الدورة',
        description: 'فشل في تحميل بيانات الدورة',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchVideoComments = async (videoId) => {
    if (!videoId || !courseData || !user) return;

    try {
      setIsLoadingComments(true);
      let url = user.role == "instructor" ? "/video-comments/instructor" : "/video-comments/my"
      const response = await axiosInstance.get(url, {
        params: {
          course: courseData._id
        }
      });

      if (response.data && response.data.comments) {
        const videoComments = response.data.comments.filter(comment =>
          comment.videoId === videoId
        );
        setComments(videoComments);
      } else {
        setComments([]);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      setComments([]);
    } finally {
      setIsLoadingComments(false);
    }
  };

  // Fetch homework data for current video
  const fetchHomeworkData = async (videoId) => {
    if (!videoId || !courseData || !user) return;
    setIsLoadingHomework(true);
    try {
      if (user.role === 'student') {
        const res = await axiosInstance.get(`/video-homework/${videoId}/my-reply`, {
          params: { courseId: courseData._id }
        });
        setMyHomeworkReply(res.data.data);
      } else if (user.role === 'admin' || user.role === 'instructor') {
        const res = await axiosInstance.get(`/video-homework/${videoId}/replies`, {
          params: { courseId: courseData._id }
        });
        setAllHomeworkReplies(res.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching homework data:', err);
    } finally {
      setIsLoadingHomework(false);
    }
  };

  // Student submits homework photos
  const handleHomeworkSubmit = async () => {
    if (!homeworkPhotos || homeworkPhotos.length === 0 || !selectedVideo || !courseData) return;
    setIsSubmittingHomework(true);
    try {
      const formData = new FormData();
      Array.from(homeworkPhotos).forEach((file) => formData.append('photos', file));
      formData.append('videoId', selectedVideo._id);
      formData.append('courseId', courseData._id);
      formData.append('videoName', selectedVideo.nomvideo || selectedVideo.title || 'فيديو');
      const res = await axiosInstance.post('/video-homework/submit', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setMyHomeworkReply(res.data.data);
      setHomeworkPhotos([]);
      toast({
        title: 'تم إرسال واجبك بنجاح ✅',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'خطأ',
        description: err.response?.data?.message || 'فشل في رفع الصور',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSubmittingHomework(false);
    }
  };

  // Admin/instructor sends SMS reminder
  const handleSendSms = async (sendTo) => {
    if (!selectedVideo || !courseData) return;
    setIsSendingSms(true);
    try {
      const res = await axiosInstance.post('/video-homework/send-sms', {
        videoId: selectedVideo._id,
        courseId: courseData._id,
        videoName: selectedVideo.nomvideo || selectedVideo.title || 'فيديو',
        sendTo,
      });
      toast({
        title: res.data.message,
        status: res.data.success ? 'success' : 'warning',
        duration: 4000,
        isClosable: true,
      });
      setIsSmsModalOpen(false);
    } catch (err) {
      toast({
        title: 'خطأ في الإرسال',
        description: err.response?.data?.message || 'فشل في إرسال الرسائل',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSendingSms(false);
    }
  };

  const fetchUserComments = async (userId, videoId = null) => {
    if (!userId || !courseData) return;

    try {
      setIsLoadingUserComments(true);

      const options = {
        course: courseData._id,
        ...(videoId && { video: videoId })
      };

      const response = await getUserComments(userId, options);

      if (response && response.comments) {
        setUserComments(response.comments);
      } else {
        setUserComments([]);
      }
    } catch (error) {
      console.error('Error fetching user comments:', error);
      setUserComments([]);
      toast({
        title: 'Error',
        description: 'Failed to fetch user comments',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingUserComments(false);
    }
  };

  const handleUserSelect = (userId) => {
    setSelectedUserId(userId);
    setShowUserComments(true);
    fetchUserComments(userId, selectedVideo?._id);
  };

  const handleVideoSelect = (video, chapterIndex, partIndex, videoIndex) => {
    setSelectedVideo({
      ...video,
      chapterIndex,
      partIndex,
      videoIndex
    });
    setCurrentChapter(chapterIndex);
    setCurrentPart(partIndex);
  };

  const handleNextVideo = () => {
    if (!courseData || !selectedVideo) return;

    const currentChapterData = courseData.chapitres[selectedVideo.chapterIndex];
    const currentPartData = currentChapterData.parties[selectedVideo.partIndex];

    if (selectedVideo.videoIndex < currentPartData.videos.length - 1) {
      const nextVideo = currentPartData.videos[selectedVideo.videoIndex + 1];
      handleVideoSelect(nextVideo, selectedVideo.chapterIndex, selectedVideo.partIndex, selectedVideo.videoIndex + 1);
      return;
    }

    if (selectedVideo.partIndex < currentChapterData.parties.length - 1) {
      const nextPart = currentChapterData.parties[selectedVideo.partIndex + 1];
      if (nextPart.videos.length > 0) {
        handleVideoSelect(nextPart.videos[0], selectedVideo.chapterIndex, selectedVideo.partIndex + 1, 0);
        return;
      }
    }

    if (selectedVideo.chapterIndex < courseData.chapitres.length - 1) {
      const nextChapter = courseData.chapitres[selectedVideo.chapterIndex + 1];
      if (nextChapter.isOpen && nextChapter.parties.length > 0 && nextChapter.parties[0].videos.length > 0) {
        handleVideoSelect(nextChapter.parties[0].videos[0], selectedVideo.chapterIndex + 1, 0, 0);
      }
    }
  };

  const handlePreviousVideo = () => {
    if (!courseData || !selectedVideo) return;

    if (selectedVideo.videoIndex > 0) {
      const currentPartData = courseData.chapitres[selectedVideo.chapterIndex].parties[selectedVideo.partIndex];
      const prevVideo = currentPartData.videos[selectedVideo.videoIndex - 1];
      handleVideoSelect(prevVideo, selectedVideo.chapterIndex, selectedVideo.partIndex, selectedVideo.videoIndex - 1);
      return;
    }

    if (selectedVideo.partIndex > 0) {
      const currentChapterData = courseData.chapitres[selectedVideo.chapterIndex];
      const prevPart = currentChapterData.parties[selectedVideo.partIndex - 1];
      if (prevPart.videos.length > 0) {
        handleVideoSelect(prevPart.videos[prevPart.videos.length - 1], selectedVideo.chapterIndex, selectedVideo.partIndex - 1, prevPart.videos.length - 1);
        return;
      }
    }

    if (selectedVideo.chapterIndex > 0) {
      const prevChapter = courseData.chapitres[selectedVideo.chapterIndex - 1];
      if (prevChapter.isOpen && prevChapter.parties.length > 0) {
        const lastPart = prevChapter.parties[prevChapter.parties.length - 1];
        if (lastPart.videos.length > 0) {
          handleVideoSelect(lastPart.videos[lastPart.videos.length - 1], selectedVideo.chapterIndex - 1, prevChapter.parties.length - 1, lastPart.videos.length - 1);
        }
      }
    }
  };

  const handleCommentSubmit = async () => {
    if (!comment.trim() || !selectedVideo || !courseData || !user) return;

    try {
      const currentChapter = courseData.chapitres[selectedVideo.chapterIndex];
      const currentPart = currentChapter.parties[selectedVideo.partIndex];

      const commentData = {
        videoId: selectedVideo._id,
        courseId: courseData._id,
        chapterId: currentChapter._id,
        partieId: currentPart._id,
        comment: comment,
        type: 'general',
        priority: 'medium',
        videoTitle: selectedVideo.nomvideo || selectedVideo.title,
        videoUrl: selectedVideo.url
      };

      const response = await axiosInstance.post(`/video-comments`, commentData);

      if (response.data) {
        const newComment = {
          ...response.data,
          user: {
            name: user.name || user.username,
            _id: user._id
          }
        };

        setComments(prevComments => [newComment, ...prevComments]);
        setComment('');

        toast({
          title: 'تم إرسال التعليق',
          description: 'تم إضافة تعليقك بنجاح',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error submitting comment:', error);
      toast({
        title: 'خطأ في إرسال التعليق',
        description: error.response?.data?.message || 'فشل في إرسال التعليق، يرجى المحاولة مرة أخرى',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleReplySubmit = async (commentId, replyMessage) => {
    if (!replyMessage.trim() || !user) return;

    try {
      const isInstructor = user.role === 'instructor' ||
        user.username === courseData.teacherName ||
        user._id === courseData.teacherId;

      const response = await axiosInstance.post(`/video-comments/${commentId}/reply`, {
        comment: replyMessage,
        senderId: user._id,
        sender: user.username || user.name,
        isInstructor: isInstructor
      });

      if (response.data && response.data.success) {
        setComments(prevComments =>
          prevComments.map(comment =>
            comment._id === commentId
              ? {
                ...comment,
                replies: [...(comment.replies || []), response.data.reply],
                status: isInstructor ? 'replied' : comment.status
              }
              : comment
          )
        );

        toast({
          title: 'تم إرسال الرد',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error submitting reply:', error);
      toast({
        title: 'خطأ في إرسال الرد',
        description: 'فشل في إرسال الرد، يرجى المحاولة مرة أخرى',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleAttachmentDownload = async (attachment) => {
    try {
      // Open the PDF in a new tab (_blank)
      const pdfUrl = urlvideo + attachment.url;
      window.open(pdfUrl, '_blank');
      toast({
        title: 'تم تحميل الملف',
        description: `تم تحميل ${attachment.nom} بنجاح`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error downloading attachment:', error);
      toast({
        title: 'خطأ في تحميل الملف',
        description: 'فشل في تحميل الملف، يرجى المحاولة مرة أخرى',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Hierarchical navigation functions
  const navigateToChapter = (chapterIndex) => {
    setSelectedChapterForNav(chapterIndex);
    setSidebarView('parts');
  };

  const navigateToPart = (chapterIndex, partIndex) => {
    setSelectedChapterForNav(chapterIndex);
    setSelectedPartForNav(partIndex);
    setSidebarView('videos');
  };

  const navigateBack = () => {
    if (sidebarView === 'videos') {
      setSidebarView('parts');
      setSelectedPartForNav(null);
    } else if (sidebarView === 'parts') {
      setSidebarView('chapters');
      setSelectedChapterForNav(null);
    }
  };

  const navigateToChapters = () => {
    setSidebarView('chapters');
    setSelectedChapterForNav(null);
    setSelectedPartForNav(null);
  };

  const getTotalVideos = () => {
    return courseData?.chapitres?.reduce((acc, ch) =>
      acc + (ch.parties?.reduce((pAcc, p) => pAcc + p.videos.length, 0) || 0), 0) || 0;
  };

  const getCurrentVideoNumber = () => {
    if (!selectedVideo || !courseData) return 1;

    let count = 1;
    for (let i = 0; i < selectedVideo.chapterIndex; i++) {
      const chapter = courseData.chapitres[i];
      count += chapter.parties?.reduce((acc, p) => acc + p.videos.length, 0) || 0;
    }

    for (let i = 0; i < selectedVideo.partIndex; i++) {
      const part = courseData.chapitres[selectedVideo.chapterIndex].parties[i];
      count += part.videos.length;
    }

    count += selectedVideo.videoIndex;
    return count;
  };

  const getTotalDuration = () => {
    if (!courseData?.chapitres) return 'غير محدد';

    let totalMinutes = 0;
    courseData.chapitres.forEach(chapter => {
      chapter.parties?.forEach(part => {
        part.videos?.forEach(video => {
          if (video.duration) {
            const duration = video.duration.toString();
            if (duration.includes(':')) {
              const [minutes, seconds] = duration.split(':').map(Number);
              totalMinutes += minutes + (seconds / 60);
            } else {
              totalMinutes += parseInt(duration) || 0;
            }
          }
        });
      });
    });

    if (totalMinutes === 0) return courseData.duration || 'غير محدد';

    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.round(totalMinutes % 60);

    if (hours > 0) {
      return `${hours} ساعة و ${minutes} دقيقة`;
    }
    return `${minutes} دقيقة`;
  };

  // Enhanced Loading State
  if (isLoading) {
    return (
      <Box dir="rtl" fontFamily="Cairo, sans-serif">
        <Flex h="100vh" overflowX="hidden">
          <Sidebar />
          <Box
            flex="1"
            ml={contentMarginLeft}
            p={6}
            background={bgColor}
            transition="margin-left 0.3s ease"
          >
            <Container maxW="8xl">
              <VStack spacing={8} align="stretch">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <Skeleton
                    height="100px"
                    borderRadius="3xl"
                    startColor="purple.100"
                    endColor="blue.100"
                  />
                </motion.div>
                <Flex direction={{ base: 'column', lg: 'row' }} gap={8}>
                  <Box w={{ base: 'full', lg: '380px' }} flexShrink={0}>
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.7, delay: 0.2 }}
                    >
                      <Skeleton
                        height="500px"
                        borderRadius="3xl"
                        startColor="purple.100"
                        endColor="blue.100"
                      />
                    </motion.div>
                  </Box>
                  <Box flex="1">
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.7, delay: 0.4 }}
                    >
                      <Skeleton
                        height="600px"
                        borderRadius="3xl"
                        startColor="purple.100"
                        endColor="blue.100"
                        mb={6}
                      />
                      <SkeletonText
                        mt={4}
                        noOfLines={8}
                        spacing={4}
                        startColor="purple.100"
                        endColor="blue.100"
                      />
                    </motion.div>
                  </Box>
                </Flex>
              </VStack>
            </Container>
          </Box>
        </Flex>
      </Box>
    );
  }

  // Enhanced Error State
  if (!courseData) {
    return (
      <Box dir="rtl" fontFamily="Cairo, sans-serif">
        <Flex h="100vh" overflowX="hidden">
          <Sidebar />
          <Box
            flex="1"
            ml={contentMarginLeft}
            p={6}
            background={bgColor}
            transition="margin-left 0.3s ease"
          >
            <Center h="60vh">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
              >
                <VStack spacing={6}>
                  <Box
                    p={6}
                    borderRadius="full"
                    bgGradient="linear(to-r, red.100, pink.100)"
                  >
                    <Icon as={FiBook} w={20} h={20} color="red.400" />
                  </Box>
                  <Heading size="xl" color="gray.600" textAlign="center">
                    الدورة غير موجودة
                  </Heading>
                  <Text color="gray.500" fontSize="lg" textAlign="center">
                    لم يتم العثور على الدورة المطلوبة
                  </Text>
                  <Button
                    as={RouterLink}
                    to="/courses"
                    bgGradient="linear(to-r, purple.400, blue.400)"
                    color="white"
                    _hover={{
                      bgGradient: "linear(to-r, purple.500, blue.500)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 20px rgba(128,90,213,0.3)"
                    }}
                    rightIcon={<FiArrowRight />}
                    size="lg"
                    borderRadius="full"
                    px={8}
                    transition="all 0.3s ease"
                  >
                    العودة للدورات
                  </Button>
                </VStack>
              </motion.div>
            </Center>
          </Box>
        </Flex>
      </Box>
    );
  }

  return (
    <Box dir="rtl" fontFamily="Cairo, sans-serif">
      <Flex h="100vh" overflowX="hidden">
        <Sidebar />

        <Box
          flex="1"
          ml={contentMarginLeft}
          background={bgColor}
          transition="margin-left 0.3s ease"
          overflowY="auto"
          position="relative"
        >
          {/* Enhanced Header Section */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <Box
              bgGradient="linear(135deg, #667eea 0%, #764ba2 100%)"
              color="white"
              py={16}
              px={6}
              position="relative"
              overflow="hidden"
              _before={{
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
                animation: 'float 6s ease-in-out infinite'
              }}
            >
              <Container maxW="8xl" position="relative" zIndex={1}>
                <VStack spacing={8} align="stretch">
                  <motion.div variants={slideIn}>
                    <HStack spacing={3} fontSize="sm" opacity="0.9">
                      <Text
                        _hover={{
                          color: "yellow.200",
                          cursor: 'pointer',
                          transform: "translateX(-2px)"
                        }}
                        transition="all 0.2s ease"
                      >
                        الدورات
                      </Text>
                      <Icon as={FiChevronRight} />
                      <Text color="yellow.200" fontWeight="600">
                        {courseData.name}
                      </Text>
                    </HStack>
                  </motion.div>

                  <motion.div variants={staggerContainer}>
                    <VStack align="flex-start" spacing={6}>
                      <motion.div variants={fadeIn}>
                        <HStack spacing={4} align="center" flexWrap="wrap">
                          <Heading
                            as="h1"
                            size="3xl"
                            fontWeight="900"
                            lineHeight="1.1"
                            bgGradient="linear(to-r, white, yellow.200)"
                            bgClip="text"
                            textShadow="0 2px 4px rgba(0,0,0,0.1)"
                          >
                            {courseData.name}
                          </Heading>
                          <Badge
                            bgGradient="linear(to-r, yellow.400, orange.400)"
                            color="white"
                            px={4}
                            py={2}
                            borderRadius="full"
                            fontSize="md"
                            fontWeight="700"
                            boxShadow="0 4px 12px rgba(0,0,0,0.2)"
                            _hover={{
                              transform: "scale(1.05)",
                              boxShadow: "0 6px 16px rgba(0,0,0,0.3)"
                            }}
                            transition="all 0.2s ease"
                          >
                            {courseData.category}
                          </Badge>
                        </HStack>
                      </motion.div>

                      <motion.div variants={fadeIn}>
                        <HStack spacing={6} flexWrap="wrap">
                          <HStack spacing={3}>
                            <Avatar
                              src="/assets/oussama-loussifi.jpeg"
                              size="md"
                              border="3px solid"
                              borderColor="rgba(255,255,255,0.3)"
                              boxShadow="0 4px 12px rgba(0,0,0,0.2)"
                            />
                            <VStack align="flex-start" spacing={1}>
                              <Text fontWeight="700" fontSize="lg" color="white">
                                {courseData.teacherName}
                              </Text>
                              <Text fontSize="sm" opacity="0.8" color="yellow.100">
                                أستاذ
                              </Text>
                            </VStack>
                          </HStack>

                          <Box h="50px" w="1px" bg="rgba(255,255,255,0.3)" />

                          <HStack spacing={6} flexWrap="wrap">
                            <motion.div
                              whileHover={{ scale: 1.05 }}
                              transition={{ duration: 0.2 }}
                            >
                              <HStack spacing={2}>
                                <Box
                                  p={2}
                                  borderRadius="lg"
                                  bg="rgba(255,255,255,0.1)"
                                  backdropFilter="blur(10px)"
                                >
                                  <Icon as={FiUsers} boxSize={5} />
                                </Box>
                                <VStack align="flex-start" spacing={0}>
                                  <Text fontSize="lg" fontWeight="700">
                                    {courseData.enrolledStudents || 0}
                                  </Text>
                                  <Text fontSize="xs" opacity="0.8">
                                    طالب
                                  </Text>
                                </VStack>
                              </HStack>
                            </motion.div>

                            <motion.div
                              whileHover={{ scale: 1.05 }}
                              transition={{ duration: 0.2 }}
                            >
                              <HStack spacing={2}>
                                <Box
                                  p={2}
                                  borderRadius="lg"
                                  bg="rgba(255,255,255,0.1)"
                                  backdropFilter="blur(10px)"
                                >
                                  <Icon as={FiClock} boxSize={5} />
                                </Box>
                                <VStack align="flex-start" spacing={0}>
                                  <Text fontSize="lg" fontWeight="700">
                                    {getTotalDuration()}
                                  </Text>
                                  <Text fontSize="xs" opacity="0.8">
                                    مدة الدورة
                                  </Text>
                                </VStack>
                              </HStack>
                            </motion.div>

                            <motion.div
                              whileHover={{ scale: 1.05 }}
                              transition={{ duration: 0.2 }}
                            >
                              <HStack spacing={2}>
                                <Box
                                  p={2}
                                  borderRadius="lg"
                                  bg="rgba(255,255,255,0.1)"
                                  backdropFilter="blur(10px)"
                                >
                                  <Icon as={FiStar} boxSize={5} color="yellow.300" />
                                </Box>
                                <VStack align="flex-start" spacing={0}>
                                  <Text fontSize="lg" fontWeight="700">
                                    {courseData.rating || 4.8}
                                  </Text>
                                  <Text fontSize="xs" opacity="0.8">
                                    تقييم
                                  </Text>
                                </VStack>
                              </HStack>
                            </motion.div>
                          </HStack>
                        </HStack>
                      </motion.div>
                    </VStack>
                  </motion.div>
                </VStack>
              </Container>
            </Box>
          </motion.div>

          {/* Enhanced Main Content */}
          <Container maxW="8xl" py={10}>
            <Flex spacing={10} gap={10} direction={{ base: 'column', lg: 'row' }}>
              {/* Enhanced Course Curriculum Sidebar - Multi-Pane Navigation */}
              <motion.div
                variants={slideIn}
                initial="hidden"
                animate="visible"
              >
                <Box w={{ base: 'full', lg: selectedPartForNav !== null ? '1000px' : selectedChapterForNav !== null ? '750px' : '400px' }} flexShrink={0} transition="width 0.5s cubic-bezier(0.4, 0, 0.2, 1)">
                  <Box
                    bg={cardBg}
                    borderRadius="3xl"
                    boxShadow="0 25px 50px rgba(0,0,0,0.08)"
                    position="sticky"
                    top="20px"
                    border="1px solid"
                    borderColor={borderColor}
                    backdropFilter="blur(20px)"
                    overflow="hidden"
                  >
                    <Box
                      bgGradient="linear(to-r, purple.400, blue.400)"
                      color="white"
                      p={6}
                      position="relative"
                      _before={{
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 100%)',
                        pointerEvents: 'none'
                      }}
                    >
                      <HStack justify="space-between" align="center">
                        <HStack spacing={3}>
                          {isMobile && sidebarView !== 'chapters' && (
                            <IconButton
                              icon={<FiChevronRight />}
                              variant="ghost"
                              color="white"
                              _hover={{ bg: "whiteAlpha.300" }}
                              onClick={navigateBack}
                              size="sm"
                              borderRadius="full"
                              aria-label="Back"
                            />
                          )}
                          <Icon as={FiLayers} boxSize={6} />
                          <Heading size={isMobile ? "md" : "lg"} fontWeight="700">
                            {isMobile ? (
                              sidebarView === 'chapters' ? "محتوى الدورة" :
                                sidebarView === 'parts' ? courseData.chapitres[selectedChapterForNav]?.nom :
                                  courseData.chapitres[selectedChapterForNav]?.parties[selectedPartForNav]?.name
                            ) : "محتوى الدورة"}
                          </Heading>
                        </HStack>
                        {(!isMobile || sidebarView !== 'chapters') && (
                          <Button
                            size="sm"
                            variant="whiteAlpha"
                            borderRadius="full"
                            onClick={navigateToChapters}
                            leftIcon={<FiChevronRight />}
                          >
                            {isMobile ? "الرئيسية" : "الكل"}
                          </Button>
                        )}
                      </HStack>
                      <Text fontSize="sm" opacity="0.9" mt={2}>
                        {getTotalVideos()} فيديو • {getTotalDuration()}
                      </Text>
                    </Box>

                    <Box p={4}>
                      <Flex gap={4} overflowX="auto" pb={2} css={{
                        '&::-webkit-scrollbar': { height: '6px' },
                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                        '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.1)', borderRadius: '10px' },
                      }}>
                        {/* Column 1: Chapters */}
                        {(!isMobile || sidebarView === 'chapters') && (
                          <Box minW={isMobile ? "full" : "300px"} maxH="650px" overflowY="auto" flex="1">
                            <VStack spacing={3} align="stretch">
                              {!isMobile && <Heading size="sm" mb={2} color="gray.500" px={2} textAlign="right">الفصول</Heading>}
                              {courseData.chapitres?.map((chapter, chapterIndex) => {
                                const colorScheme = getChapterColorScheme();
                                const isSelected = selectedChapterForNav === chapterIndex;
                                const videoCount = chapter.parties?.reduce((acc, p) => acc + p.videos.length, 0) || 0;

                                return (
                                  <motion.div
                                    key={chapterIndex}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                  >
                                    <Box
                                      as="button"
                                      w="full"
                                      bg={isSelected ? "purple.50" : (chapter.isOpen ? "white" : "gray.50")}
                                      _dark={{ bg: isSelected ? "purple.900" : (chapter.isOpen ? "gray.700" : "gray.800") }}
                                      borderRadius="xl"
                                      p={4}
                                      onClick={() => chapter.isOpen && navigateToChapter(chapterIndex)}
                                      transition="all 0.3s ease"
                                      boxShadow={isSelected ? "0 4px 12px rgba(102,126,234,0.15)" : "0 2px 8px rgba(0,0,0,0.05)"}
                                      border="2px solid"
                                      borderColor={isSelected ? "purple.400" : (chapter.isOpen ? "purple.100" : "gray.200")}
                                      opacity={chapter.isOpen ? 1 : 0.6}
                                      cursor={chapter.isOpen ? "pointer" : "not-allowed"}
                                      textAlign="right"
                                    >
                                      <VStack align="stretch" spacing={2}>
                                        <HStack justify="space-between" w="full">
                                          <Badge bgGradient={colorScheme.chapter} color="white" px={2} py={0.5} borderRadius="full" fontSize="2xs">
                                            CH {chapterIndex + 1}
                                          </Badge>
                                          {chapter.isOpen ? <Icon as={FiUnlock} color="green.500" boxSize={3} /> : <Icon as={FiLock} color="red.400" boxSize={3} />}
                                        </HStack>
                                        <Text fontWeight="700" fontSize="md" noOfLines={1} color={isSelected ? "purple.600" : "gray.800"} _dark={{ color: isSelected ? "purple.200" : "white" }}>
                                          {chapter.nom}
                                        </Text>
                                        <HStack spacing={2} fontSize="xs" color="gray.500">
                                          <Text>{chapter.parties?.length || 0} P</Text>
                                          <Box w="1px" h="10px" bg="gray.300" />
                                          <Text>{videoCount} V</Text>
                                        </HStack>
                                      </VStack>
                                    </Box>
                                  </motion.div>
                                );
                              })}
                            </VStack>
                          </Box>
                        )}

                        {/* Column 2: Parts (Only if a chapter is selected) */}
                        <AnimatePresence>
                          {selectedChapterForNav !== null && (!isMobile || sidebarView === 'parts') && (
                            <motion.div
                              initial={{ opacity: 0, x: 20 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: 20 }}
                              style={{ display: 'flex', flexDirection: 'column', minWidth: isMobile ? '100%' : '300px', flex: 1 }}
                            >
                              <Box maxH="650px" overflowY="auto">
                                <VStack spacing={3} align="stretch">
                                  {!isMobile && <Heading size="sm" mb={2} color="gray.500" px={2} textAlign="right">الأجزاء</Heading>}
                                  {courseData.chapitres[selectedChapterForNav]?.parties?.map((part, partIndex) => {
                                    const isSelected = selectedPartForNav === partIndex;
                                    const videoCount = part.videos?.length || 0;

                                    return (
                                      <motion.div key={partIndex} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                                        <Box
                                          as="button"
                                          w="full"
                                          bg={isSelected ? "pink.50" : "white"}
                                          _dark={{ bg: isSelected ? "pink.900" : "gray.700" }}
                                          borderRadius="xl"
                                          p={4}
                                          onClick={() => navigateToPart(selectedChapterForNav, partIndex)}
                                          transition="all 0.2s ease"
                                          boxShadow={isSelected ? "0 4px 12px rgba(240,147,251,0.15)" : "0 2px 8px rgba(0,0,0,0.05)"}
                                          border="2px solid"
                                          borderColor={isSelected ? "pink.400" : "pink.100"}
                                        >
                                          <VStack align="stretch" spacing={2} textAlign="right">
                                            <HStack justify="space-between">
                                              <Badge bgGradient={getChapterColorScheme().part} color="white" px={2} py={0.5} borderRadius="full" fontSize="2xs">
                                                P {partIndex + 1}
                                              </Badge>
                                              <Icon as={FiChevronLeft} color="pink.400" boxSize={4} />
                                            </HStack>
                                            <Text fontWeight="700" fontSize="md" noOfLines={1} color={isSelected ? "pink.600" : "gray.800"} _dark={{ color: isSelected ? "pink.200" : "white" }}>
                                              {part.name}
                                            </Text>
                                            <Text fontSize="xs" color="gray.500">{videoCount} Vidéos</Text>
                                          </VStack>
                                        </Box>
                                      </motion.div>
                                    );
                                  })}
                                </VStack>
                              </Box>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        {/* Column 3: Videos (Only if a part is selected) */}
                        <AnimatePresence>
                          {selectedPartForNav !== null && selectedChapterForNav !== null && (!isMobile || sidebarView === 'videos') && (
                            <motion.div
                              initial={{ opacity: 0, x: 20 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: 20 }}
                              style={{ display: 'flex', flexDirection: 'column', minWidth: isMobile ? '100%' : '350px', flex: 1.2 }}
                            >
                              <Box maxH="650px" overflowY="auto">
                                <VStack spacing={3} align="stretch">
                                  {!isMobile && <Heading size="sm" mb={2} color="gray.500" px={2} textAlign="right">الفيديوهات</Heading>}
                                  {courseData.chapitres[selectedChapterForNav]?.parties[selectedPartForNav]?.videos?.map((video, videoIndex) => {
                                    const isSelected = selectedVideo &&
                                      selectedVideo.chapterIndex === selectedChapterForNav &&
                                      selectedVideo.partIndex === selectedPartForNav &&
                                      selectedVideo.videoIndex === videoIndex;

                                    return (
                                      <motion.div key={videoIndex} whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
                                        <Box
                                          as="button"
                                          w="full"
                                          p={3}
                                          bg={isSelected ? "blue.50" : "rgba(255,255,255,0.6)"}
                                          _dark={{ bg: isSelected ? "blue.900" : "rgba(45,55,72,0.6)" }}
                                          borderRadius="lg"
                                          onClick={() => handleVideoSelect(video, selectedChapterForNav, selectedPartForNav, videoIndex)}
                                          border="1px solid"
                                          borderColor={isSelected ? "blue.400" : "transparent"}
                                          textAlign="right"
                                        >
                                          <HStack spacing={3} align="start">
                                            <Box p={2} borderRadius="lg" bg={isSelected ? "blue.400" : "gray.100"} color={isSelected ? "white" : "gray.400"}>
                                              <Icon as={isSelected ? FiPlay : FiPlayCircle} boxSize={4} />
                                            </Box>
                                            <VStack align="flex-start" spacing={1} flex="1">
                                              <Text fontSize="sm" fontWeight={isSelected ? "700" : "600"} color={isSelected ? "blue.600" : "gray.700"} _dark={{ color: isSelected ? "blue.200" : "gray.200" }} noOfLines={2}>
                                                {video.nomvideo}
                                              </Text>
                                              <HStack spacing={2} fontSize="2xs" color="gray.500">
                                                <Icon as={FiClock} />
                                                <Text>{video.duree || "05:00"}</Text>
                                                {completedVideos.has(`${selectedChapterForNav}-${selectedPartForNav}-${videoIndex}`) && (
                                                  <Icon as={FiCheckCircle} color="green.500" />
                                                )}
                                              </HStack>
                                            </VStack>
                                          </HStack>
                                        </Box>
                                      </motion.div>
                                    );
                                  })}

                                  {/* Attachments within the Video column */}
                                  {courseData.chapitres[selectedChapterForNav]?.parties[selectedPartForNav]?.attachments?.length > 0 && (
                                    <Box mt={4} pt={4} borderTop="1px solid" borderColor="gray.100">
                                      <Heading size="xs" mb={3} color="teal.500" textAlign="right">الملفات المرفقة</Heading>
                                      <VStack spacing={2} align="stretch">
                                        {courseData.chapitres[selectedChapterForNav]?.parties[selectedPartForNav]?.attachments.map((att, idx) => (
                                          <AttachmentItem key={idx} attachment={att} onDownload={handleAttachmentDownload} />
                                        ))}
                                      </VStack>
                                    </Box>
                                  )}
                                </VStack>
                              </Box>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </Flex>
                    </Box>
                  </Box>
                </Box>
              </motion.div>

              {/* Enhanced Main Video Content */}
              <motion.div
                variants={fadeIn}
                initial="hidden"
                animate="visible"
                style={{ flex: 1 }}
              >
                <VStack spacing={8} align="stretch">
                  {/* Enhanced Video Player Section */}
                  <Box>
                    <motion.div
                      whileHover={{ scale: 1.005 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Box
                        bg="linear-gradient(135deg, #000000 0%, #1a1a1a 100%)"
                        borderRadius="3xl"
                        overflow="hidden"
                        position="relative"
                        mb={6}
                        boxShadow="0 25px 50px rgba(0,0,0,0.25)"
                        border="1px solid"
                        borderColor="rgba(255,255,255,0.1)"
                        _before={{
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          borderRadius: '3xl',
                          padding: '2px',
                          background: 'linear-gradient(135deg, rgba(102,126,234,0.3), rgba(118,75,162,0.3))',
                          mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                          maskComposite: 'exclude',
                          zIndex: -1
                        }}
                      >
                        <AspectRatio ratio={16 / 9}>
                          {selectedVideo ? (
                            <VideoPlayer
                              src={selectedVideo.url}
                              poster={selectedVideo.thumbnail}
                              idvdocipher={selectedVideo.idvdocipher}
                              videoId={selectedVideo.idvdocipher}
                              video={selectedVideo}
                              style={{
                                width: "100%",
                                height: "100%",
                                borderRadius: "3xl"
                              }}
                            />
                          ) : (
                            <Center bg="linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)" color="white">
                              <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6 }}
                              >
                                <VStack spacing={6}>
                                  <Box
                                    p={6}
                                    borderRadius="full"
                                    bgGradient="linear(to-r, purple.400, blue.400)"
                                    boxShadow="0 8px 20px rgba(102,126,234,0.3)"
                                  >
                                    <Icon as={FiPlay} boxSize={20} />
                                  </Box>
                                  <Text fontSize="xl" fontWeight="600">
                                    اختر فيديو لبدء التعلم
                                  </Text>
                                </VStack>
                              </motion.div>
                            </Center>
                          )}
                        </AspectRatio>
                      </Box>
                    </motion.div>

                    {/* Enhanced Video Information */}
                    {selectedVideo && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                      >
                        <VStack align="stretch" spacing={6}>
                          <Box
                            p={8}
                            bg={cardBg}
                            borderRadius="3xl"
                            boxShadow="0 15px 35px rgba(0,0,0,0.08)"
                            border="1px solid"
                            borderColor={borderColor}
                            backdropFilter="blur(20px)"
                            position="relative"
                            overflow="hidden"
                            _before={{
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'radial-gradient(circle at 10% 20%, rgba(102,126,234,0.03) 0%, transparent 50%), radial-gradient(circle at 90% 80%, rgba(240,147,251,0.03) 0%, transparent 50%)',
                              pointerEvents: 'none'
                            }}
                          >
                            <VStack align="stretch" spacing={6} position="relative" zIndex={1}>
                              {/* 72h SMS Button - only for admin/instructor */}
                              {selectedVideo && (user?.role === 'admin' || user?.role === 'instructor') && selectedVideo.createdAt && (
                                Date.now() - new Date(selectedVideo.createdAt).getTime() >= 72 * 60 * 60 * 1000
                              ) && (
                                  <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.4 }}
                                  >
                                    <Button
                                      onClick={() => setIsSmsModalOpen(true)}
                                      bgGradient="linear(to-r, orange.400, red.400)"
                                      color="white"
                                      size="md"
                                      borderRadius="full"
                                      leftIcon={<span>📱</span>}
                                      _hover={{
                                        bgGradient: 'linear(to-r, orange.500, red.500)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 8px 20px rgba(237,100,40,0.35)',
                                      }}
                                      transition="all 0.2s ease"
                                      fontWeight="700"
                                      px={6}
                                      boxShadow="0 4px 14px rgba(237,100,40,0.25)"
                                    >
                                      إرسال رسالة SMS للغائبين
                                    </Button>
                                  </motion.div>
                                )}
                              <Heading
                                size="xl"
                                fontWeight="700"
                                lineHeight="1.3"
                                color={textColor}
                                bgGradient="linear(to-r, purple.600, blue.600)"
                                bgClip="text"
                              >
                                {selectedVideo.title || selectedVideo.nomvideo}
                              </Heading>

                              {/* Enhanced Video Navigation */}
                              <HStack justify="space-between" align="center" flexWrap="wrap">
                                <HStack spacing={4}>
                                  <Button
                                    leftIcon={<FiChevronRight />}
                                    variant="outline"
                                    size="md"
                                    onClick={handlePreviousVideo}
                                    borderRadius="full"
                                    borderColor="purple.200"
                                    color="purple.600"
                                    _hover={{
                                      bg: "purple.50",
                                      borderColor: "purple.400",
                                      transform: "translateY(-2px)",
                                      boxShadow: "0 6px 15px rgba(128,90,213,0.2)"
                                    }}
                                    _dark={{
                                      borderColor: "purple.400",
                                      color: "purple.300",
                                      _hover: { bg: "purple.900" }
                                    }}
                                    transition="all 0.2s ease"
                                    fontWeight="600"
                                    px={6}
                                  >
                                    السابق
                                  </Button>
                                  <Button
                                    rightIcon={<FiChevronLeft />}
                                    variant="outline"
                                    size="md"
                                    onClick={handleNextVideo}
                                    borderRadius="full"
                                    borderColor="blue.200"
                                    color="blue.600"
                                    _hover={{
                                      bg: "blue.50",
                                      borderColor: "blue.400",
                                      transform: "translateY(-2px)",
                                      boxShadow: "0 6px 15px rgba(79,172,254,0.2)"
                                    }}
                                    _dark={{
                                      borderColor: "blue.400",
                                      color: "blue.300",
                                      _hover: { bg: "blue.900" }
                                    }}
                                    transition="all 0.2s ease"
                                    fontWeight="600"
                                    px={6}
                                  >
                                    التالي
                                  </Button>
                                </HStack>

                                <Box
                                  px={5}
                                  py={3}
                                  bgGradient="linear(to-r, purple.100, blue.100)"
                                  _dark={{ bgGradient: "linear(to-r, purple.800, blue.800)" }}
                                  borderRadius="full"
                                  border="1px solid"
                                  borderColor="purple.200"
                                  _dark={{ borderColor: "purple.600" }}
                                  boxShadow="0 4px 12px rgba(0,0,0,0.05)"
                                >
                                  <Text fontSize="sm" fontWeight="600" color="purple.700" _dark={{ color: "purple.200" }}>
                                    فيديو {getCurrentVideoNumber()} من {getTotalVideos()}
                                  </Text>
                                </Box>
                              </HStack>

                              {/* Enhanced Video Description */}
                              {selectedVideo.description && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  transition={{ duration: 0.4 }}
                                >
                                  <Box
                                    bg="linear-gradient(135deg, rgba(247,250,252,0.9) 0%, rgba(237,242,247,0.95) 100%)"
                                    _dark={{ bg: "linear-gradient(135deg, rgba(45,55,72,0.9) 0%, rgba(74,85,104,0.95) 100%)" }}
                                    borderRadius="xl"
                                    p={6}
                                    border="1px solid"
                                    borderColor="purple.100"
                                    _dark={{ borderColor: "purple.700" }}
                                    position="relative"
                                    _before={{
                                      content: '""',
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      w: "4px",
                                      h: "100%",
                                      bgGradient: "linear(to-b, purple.400, blue.400)",
                                      borderRadius: "full"
                                    }}
                                  >
                                    <Text
                                      fontSize="md"
                                      lineHeight="1.7"
                                      color={textColor}
                                      pl={4}
                                    >
                                      {selectedVideo.description}
                                    </Text>
                                  </Box>
                                </motion.div>
                              )}
                            </VStack>
                          </Box>
                        </VStack>
                      </motion.div>
                    )}
                  </Box>

                  {/* ===== Homework Photo Reply Section ===== */}
                  {selectedVideo && (
                    <Box
                      p={6}
                      bg={cardBg}
                      borderRadius="3xl"
                      boxShadow="0 15px 35px rgba(0,0,0,0.08)"
                      border="1px solid"
                      borderColor={borderColor}
                      backdropFilter="blur(20px)"
                    >
                      {/* STUDENT VIEW */}
                      {user?.role === 'student' && (
                        <VStack align="stretch" spacing={5}>
                          <HStack spacing={3}>
                            <Text fontSize="2xl">📸</Text>
                            <Heading size="md" fontWeight="700" bgGradient="linear(to-r, purple.500, blue.500)" bgClip="text">
                              أرسل واجبك المنزلي
                            </Heading>
                          </HStack>

                          {myHomeworkReply ? (
                            <Box>
                              <HStack mb={3}>
                                <Icon as={FiCheckCircle} color="green.400" boxSize={5} />
                                <Text color="green.600" fontWeight="600">تم إرسال واجبك بتاريخ {new Date(myHomeworkReply.submittedAt).toLocaleDateString('ar-EG')}</Text>
                              </HStack>
                              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                                {myHomeworkReply.photoUrls.map((url, i) => (
                                  <Image
                                    key={i}
                                    src={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${url}`}
                                    borderRadius="xl"
                                    objectFit="cover"
                                    h="120px"
                                    w="100%"
                                    cursor="pointer"
                                    onClick={() => window.open(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${url}`, '_blank')}
                                    boxShadow="0 4px 12px rgba(0,0,0,0.15)"
                                    _hover={{ transform: 'scale(1.03)', transition: 'all 0.2s' }}
                                    alt={`صورة الواجب ${i + 1}`}
                                  />
                                ))}
                              </SimpleGrid>
                              <Text fontSize="sm" color="gray.500" mt={3}>يمكنك رفع صور إضافية أدناه</Text>
                            </Box>
                          ) : (
                            <Text color="orange.500" fontWeight="500">⚠️ لم ترسل واجبك بعد. يرجى رفع صورة.</Text>
                          )}

                          <FormControl>
                            <FormLabel fontWeight="600" color="gray.700">اختر صور الواجب (يمكنك اختيار أكثر من صورة)</FormLabel>
                            <Input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={(e) => setHomeworkPhotos(e.target.files)}
                              p={1}
                              border="2px dashed"
                              borderColor="purple.200"
                              borderRadius="xl"
                              _hover={{ borderColor: 'purple.400' }}
                              _focus={{ borderColor: 'purple.500' }}
                              height="auto"
                              py={3}
                              dir="rtl"
                            />
                          </FormControl>

                          {homeworkPhotos && homeworkPhotos.length > 0 && (
                            <Text fontSize="sm" color="purple.600" fontWeight="500">
                              تم اختيار {homeworkPhotos.length} صورة
                            </Text>
                          )}

                          <Button
                            bgGradient="linear(to-r, purple.400, blue.400)"
                            color="white"
                            borderRadius="full"
                            onClick={handleHomeworkSubmit}
                            isLoading={isSubmittingHomework}
                            isDisabled={!homeworkPhotos || homeworkPhotos.length === 0}
                            loadingText="جاري الرفع..."
                            fontWeight="700"
                            _hover={{
                              bgGradient: 'linear(to-r, purple.500, blue.500)',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 8px 20px rgba(128,90,213,0.3)',
                            }}
                            transition="all 0.2s ease"
                            px={8}
                          >
                            إرسال الواجب 📤
                          </Button>
                        </VStack>
                      )}

                      {/* ADMIN / INSTRUCTOR VIEW */}
                      {(user?.role === 'admin' || user?.role === 'instructor') && (
                        <VStack align="stretch" spacing={5}>
                          <HStack spacing={3} justify="space-between">
                            <HStack spacing={3}>
                              <Text fontSize="2xl">📋</Text>
                              <Heading size="md" fontWeight="700" bgGradient="linear(to-r, purple.500, blue.500)" bgClip="text">
                                ردود الواجب المنزلي
                              </Heading>
                              <Badge colorScheme="purple" borderRadius="full" px={3} py={1} fontWeight="700">
                                {allHomeworkReplies.length} طالب
                              </Badge>
                            </HStack>
                            <Button
                              size="sm"
                              variant="outline"
                              colorScheme="purple"
                              borderRadius="full"
                              onClick={() => fetchHomeworkData(selectedVideo._id)}
                              isLoading={isLoadingHomework}
                            >
                              تحديث
                            </Button>
                          </HStack>

                          {isLoadingHomework ? (
                            <Center py={8}><Spinner color="purple.400" size="lg" /></Center>
                          ) : allHomeworkReplies.length === 0 ? (
                            <Center py={8}>
                              <VStack spacing={3}>
                                <Text fontSize="3xl">🖼️</Text>
                                <Text color="gray.500" fontWeight="500">لا توجد ردود بعد</Text>
                              </VStack>
                            </Center>
                          ) : (
                            <VStack align="stretch" spacing={5}>
                              {allHomeworkReplies.map((reply) => (
                                <Box
                                  key={reply._id}
                                  p={5}
                                  bg="white"
                                  _dark={{ bg: 'gray.700' }}
                                  borderRadius="xl"
                                  boxShadow="0 4px 12px rgba(0,0,0,0.07)"
                                  border="1px solid"
                                  borderColor="purple.100"
                                >
                                  <HStack mb={4} spacing={3}>
                                    <Avatar size="sm" name={reply.studentId?.username} />
                                    <VStack align="flex-start" spacing={0}>
                                      <Text fontWeight="700" fontSize="md" color="gray.800" _dark={{ color: 'white' }}>
                                        {reply.studentId?.username}
                                      </Text>
                                      <Text fontSize="xs" color="gray.500">
                                        أرسل في {new Date(reply.submittedAt).toLocaleString('ar-EG')}
                                      </Text>
                                    </VStack>
                                    <Badge colorScheme="green" borderRadius="full" px={3} mr="auto">
                                      ✅ أرسل
                                    </Badge>
                                  </HStack>
                                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                                    {reply.photoUrls.map((url, i) => (
                                      <Image
                                        key={i}
                                        src={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${url}`}
                                        borderRadius="xl"
                                        objectFit="cover"
                                        h="120px"
                                        w="100%"
                                        cursor="pointer"
                                        onClick={() => window.open(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${url}`, '_blank')}
                                        boxShadow="0 4px 12px rgba(0,0,0,0.15)"
                                        _hover={{ transform: 'scale(1.03)', transition: 'all 0.2s' }}
                                        alt={`واجب ${reply.studentId?.username} - ${i + 1}`}
                                      />
                                    ))}
                                  </SimpleGrid>
                                </Box>
                              ))}
                            </VStack>
                          )}
                        </VStack>
                      )}
                    </Box>
                  )}

                  {/* ===== SMS Reminder Modal ===== */}
                  <Modal isOpen={isSmsModalOpen} onClose={() => setIsSmsModalOpen(false)} isCentered dir="rtl" size="md">
                    <ModalOverlay backdropFilter="blur(8px)" />
                    <ModalContent borderRadius="3xl" fontFamily="Cairo, sans-serif" dir="rtl">
                      <ModalHeader
                        bgGradient="linear(to-r, orange.400, red.400)"
                        color="white"
                        borderTopRadius="3xl"
                        textAlign="right"
                        fontSize="xl"
                        fontWeight="700"
                        py={5}
                        px={6}
                      >
                        📱 إرسال رسالة تذكير SMS
                      </ModalHeader>
                      <ModalCloseButton color="white" top={4} left={4} right="auto" />
                      <ModalBody py={6} px={6}>
                        <VStack spacing={4} align="stretch">
                          <Text color="gray.600" fontSize="md" textAlign="right">
                            سيتم إرسال رسالة تذكير باللغة العربية للطلاب الذين لم يرسلوا واجباتهم بعد، عن الفيديو:
                          </Text>
                          <Box
                            p={4}
                            bg="orange.50"
                            borderRadius="xl"
                            border="1px solid"
                            borderColor="orange.200"
                          >
                            <Text fontWeight="700" color="orange.700" textAlign="right">
                              🎬 {selectedVideo?.nomvideo || selectedVideo?.title}
                            </Text>
                          </Box>
                          <Text color="gray.500" fontSize="sm" textAlign="right">
                            اختر الجهة التي تريد إرسال الرسالة إليها:
                          </Text>
                        </VStack>
                      </ModalBody>
                      <ModalFooter gap={3} justifyContent="center" pb={6}>
                        <Button
                          bgGradient="linear(to-r, blue.400, purple.400)"
                          color="white"
                          borderRadius="full"
                          fontWeight="700"
                          px={7}
                          isLoading={isSendingSms}
                          loadingText="جاري الإرسال..."
                          onClick={() => handleSendSms('students')}
                          _hover={{ bgGradient: 'linear(to-r, blue.500, purple.500)', transform: 'translateY(-2px)' }}
                          transition="all 0.2s"
                        >
                          👨‍🎓 إرسال للطلاب
                        </Button>
                        <Button
                          bgGradient="linear(to-r, green.400, teal.400)"
                          color="white"
                          borderRadius="full"
                          fontWeight="700"
                          px={7}
                          isLoading={isSendingSms}
                          loadingText="جاري الإرسال..."
                          onClick={() => handleSendSms('parents')}
                          _hover={{ bgGradient: 'linear(to-r, green.500, teal.500)', transform: 'translateY(-2px)' }}
                          transition="all 0.2s"
                        >
                          👨‍👩‍👦 إرسال لأولياء الأمور
                        </Button>
                      </ModalFooter>
                    </ModalContent>
                  </Modal>

                  {/* Enhanced Comments Section */}
                  {isLoadingComments ? (
                    <Center py={16}>
                      <VStack spacing={4}>
                        <Box position="relative">
                          <Spinner
                            size="xl"
                            color="purple.400"
                            thickness="4px"
                          />
                          <Box
                            position="absolute"
                            top="50%"
                            left="50%"
                            transform="translate(-50%, -50%)"
                          >
                            <Spinner
                              size="lg"
                              color="blue.400"
                              thickness="3px"
                            />
                          </Box>
                        </Box>
                        <Text color="gray.500" fontWeight="500">
                          جاري تحميل التعليقات...
                        </Text>
                      </VStack>
                    </Center>
                  ) : (
                    <YouTubeComments
                      comments={comments}
                      onCommentSubmit={handleCommentSubmit}
                      comment={comment}
                      setComment={setComment}
                      user={user}
                      courseData={courseData}
                      selectedVideo={selectedVideo}
                      onReplySubmit={handleReplySubmit}
                    />
                  )}
                </VStack>
              </motion.div>
            </Flex>
          </Container >
        </Box >
      </Flex >

      {/* Enhanced CSS Animations */}
      < style jsx > {`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-10px) rotate(1deg);
          }
        }
        
        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 0 20px rgba(102,126,234,0.3);
          }
          50% {
            box-shadow: 0 0 30px rgba(102,126,234,0.5);
          }
        }
        
        .pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }

        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 6px;
        }
        
        ::-webkit-scrollbar-track {
          background: rgba(0,0,0,0.1);
          border-radius: 10px;
        }
        
        ::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 10px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%);
        }
      `}</style >
    </Box >
  );
};

const CourseDetails = () => {
  return (
    <CourseDetailsContent />
  );
};

export default CourseDetails;

